import * as Sentry from '@sentry/react'
import { v4 as uuid } from 'uuid'

import { ROLES_WITH_ID } from '@/components/Event/NewScript/constants'
import {
  getWitnessStepPolicySkip,
  WitnessStepPolicies,
} from '@/components/Event/NewScript/hooks/state/witnessStepPolicies'
import {
  formatStepKey,
  getBaseStepKey,
  getCurrentEventIndex,
  parseStepKey,
} from '@/components/Event/NewScript/utils/stepKey'
import {
  AttendeeRole,
  DepositionResult,
  ReasonForCna,
  ScriptStep,
  ScriptStepStatus,
  TIMESTAMP_MESSAGES,
  TimestampType,
} from '@/constants'
import {
  DRScriptData,
  EventScriptDataObject,
  LowConfidenceWord,
  ReadWaiveAnswer,
  ScriptAttendee,
  ScriptData,
  ScriptDataStep,
  ScriptExhibit,
} from '@/types'
import { formatTimeInTimezone, getCurrentTimeInUtc } from '@/utils/time'

type StepAndActionKey = {
  step: string
  key: string
}

const FULLY_CANCELLED_REVIEW_ACTIONS: StepAndActionKey[] = [
  {
    step: ScriptStep.REVIEW,
    key: 'low_confidence_words',
  },
]

const areAllEventsCancelledAfterCurrentCancellation = (
  state: DRScriptData,
  currentEventIndex: number | undefined,
  firstSkippedStep: string | null,
) =>
  state.events.every((event, index) =>
    index === (currentEventIndex ?? 0)
      ? firstSkippedStep !== null
      : event.first_skipped_step !== null,
  )

const sanitizeAttendeeIdentityDocument = (
  attendee: ScriptAttendee,
): ScriptAttendee => {
  if (!ROLES_WITH_ID.includes(attendee.role)) {
    return attendee
  }

  const identityDocument = attendee.identity_document

  return {
    ...attendee,
    identity_document: identityDocument
      ? {
          status: identityDocument.status,
          document_type: identityDocument.document_type,
          number: null,
          photo_id_verified: !!identityDocument.photo_id_verified,
        }
      : null,
  }
}

const getCurrentEventState = (
  state: DRScriptData,
): {
  currentStep: string
  currentEventIndex: number | undefined
  hasEventIndex: boolean
} => {
  const { step: currentStep } = parseStepKey(state.current_step)
  const currentEventIndex = getCurrentEventIndex(
    state.current_step,
    state.events.length,
  )
  return {
    currentStep,
    currentEventIndex,
    hasEventIndex: currentEventIndex !== undefined,
  }
}

// TODO: Fix any type
export type Action = (state: DRScriptData, data?: any) => DRScriptData
export type UpdateAttendeesInput = {
  attendees: ScriptAttendee[]
  preserveExistingZoomParticipants?: boolean
}

const mergeZoomParticipants = (
  existing: ScriptAttendee['zoom_participants'] = [],
  incoming: ScriptAttendee['zoom_participants'] = [],
) => [
  ...new Map(
    [...existing, ...incoming]
      .filter(participant => participant !== null && participant !== undefined)
      .map(participant => [participant.id, participant]),
  ).values(),
]

export type StateActions = {
  completeStepAction: Action
  undoStepAction: Action
  updateAttendees: Action
  updateAttendeesAndCompleteAction: Action
  updateCurrentStep: Action
  startStep: Action
  completeStep: Action
  skipStep: Action
  updateNotes: Action
  addExhibit: Action
  updateExhibit: Action
  deleteExhibit: Action
  setNoExhibits: Action
  ensureExhibitsTracked: Action
  updateSpellings: Action
  updateTimestamps: Action
  updateAttendee: Action
  skipStepAction: Action
  addTimestamp: Action
  updateConductingData: Action
  unskipStepAction: Action
  updateOrderTranscript: Action
  updateReadWaiveAnswer: Action
  updateErrataRecipient: Action
  setFirstSkippedStep: Action
  updateEventResult: Action
  updateReasonForCna: Action
  updateOtherReason: Action
  updateCounty: Action
  updateState: Action
  updateLowConfidenceWords: Action
  completeStepAndContinue: Action
  cancelProceeding: Action
  updateGlobalState: Action
  incompleteStep: Action
  revertCanceledProceeding: Action
  updateLastTimestamp: Action
  updateCnaDeclaredAt: Action
  removeUnstartedWitnessSlice: Action
}

// Timestamps
export const addTimestamp = (
  state: DRScriptData,
  payload: {
    timestampType: TimestampType
    timezone: string
    isOnRecord?: boolean
    isCnaDeclaration?: boolean
  },
) => {
  const timestampTime = getCurrentTimeInUtc()
  const timestampText = `${formatTimeInTimezone(
    timestampTime.toISOString(),
    payload.timezone,
  )} ${TIMESTAMP_MESSAGES[payload.timestampType]}\n`

  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)
  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex)
          ? {
              ...event,
              last_timestamp: timestampTime,
              timestamps: (event.timestamps ?? '') + timestampText,
              ...(payload.isOnRecord !== undefined && {
                is_on_record: payload.isOnRecord,
              }),
              ...(payload.isCnaDeclaration !== undefined && {
                cna_declared_at: getCurrentTimeInUtc(),
              }),
            }
          : event,
      ),
    }
  }

  return _updateCurrentEvent(state, event => ({
    ...event,
    last_timestamp: timestampTime,
    timestamps: (event.timestamps ?? '') + timestampText,
    ...(payload.isOnRecord !== undefined && {
      is_on_record: payload.isOnRecord,
    }),
    ...(payload.isCnaDeclaration !== undefined && {
      cna_declared_at: getCurrentTimeInUtc(),
    }),
  }))
}

export const updateTimestamps = (state: DRScriptData, timestamps: string) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)
  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex) ? { ...event, timestamps } : event,
      ),
    }
  }

  return _updateCurrentEvent(state, event => ({ ...event, timestamps }))
}

export const updateLastTimestamp = (
  state: DRScriptData,
  lastTimestamp: string,
) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)
  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex)
          ? { ...event, last_timestamp: lastTimestamp }
          : event,
      ),
    }
  }

  return _updateCurrentEvent(state, event => ({
    ...event,
    last_timestamp: lastTimestamp,
  }))
}

export const updateCnaDeclaredAt = (
  state: DRScriptData,
  cnaDeclaredAt: string,
) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)
  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex)
          ? { ...event, cna_declared_at: cnaDeclaredAt }
          : event,
      ),
    }
  }

  return _updateCurrentEvent(state, event => ({
    ...event,
    cna_declared_at: cnaDeclaredAt,
  }))
}

const getHistoricalOnlyAttendeeIds = (
  events: EventScriptDataObject[],
  currentEventIndex?: number,
): Set<string> => {
  if (currentEventIndex === undefined) {
    return new Set()
  }

  const activeAttendeeIds = new Set(
    events
      .slice(currentEventIndex)
      .flatMap(event => event.attendees)
      .map(attendee => attendee.attendee_id),
  )

  return new Set(
    events
      .slice(0, currentEventIndex)
      .flatMap(event => event.attendees)
      .filter(attendee => !activeAttendeeIds.has(attendee.attendee_id))
      .map(attendee => attendee.attendee_id),
  )
}

const _getMasterAttendeesList = (
  events: EventScriptDataObject[],
  currentEventIndex?: number,
) => {
  const historicalOnlyAttendeeIds = getHistoricalOnlyAttendeeIds(
    events,
    currentEventIndex,
  )
  const attendeesById = new Map<string, ScriptAttendee>()
  events
    .flatMap(event => event.attendees)
    .filter(a => !historicalOnlyAttendeeIds.has(a.attendee_id))
    .forEach(a => {
      const existing = attendeesById.get(a.attendee_id)
      if (!existing) {
        attendeesById.set(a.attendee_id, a)
      }

      if (
        existing?.role === AttendeeRole.OTHER &&
        a.role !== AttendeeRole.OTHER
      ) {
        // When upgrading role from OTHER to a more specific role,
        // merge the existing data (present, zoom_participants) with the new role
        // This preserves attendance data from earlier events
        const existingParticipants = existing.zoom_participants ?? []
        const newParticipants = a.zoom_participants ?? []
        // Deduplicate zoom_participants by id
        const mergedParticipants = [
          ...existingParticipants,
          ...newParticipants.filter(
            np => !existingParticipants.some(ep => ep.id === np.id),
          ),
        ]
        attendeesById.set(a.attendee_id, {
          ...a,
          present: existing.present ?? a.present,
          zoom_participants: mergedParticipants,
        })
      }
    })
  return Array.from(attendeesById.values())
}

const _updateCurrentEvent = (
  state: DRScriptData,
  updater: (event: EventScriptDataObject) => EventScriptDataObject,
  currentEventIndex?: number,
): DRScriptData => ({
  ...state,
  events: state.events.map((event, index) =>
    index === (currentEventIndex ?? 0) ? updater(event) : event,
  ),
})

const createAction = (
  key: string,
  overrides: Partial<{
    completed: boolean
    skip: boolean
    optional: boolean
  }> = {},
) => ({
  key,
  completed: false,
  ...overrides,
})

const createWitnessStep = (
  key: ScriptStep,
  actions: Record<
    string,
    { key: string; completed: boolean; skip?: boolean; optional?: boolean }
  >,
) => ({
  key,
  status: ScriptStepStatus.INCOMPLETE,
  actions,
})

/**
 * Closing actions for a deposition that proceeds normally. Their default skip
 * comes from `getWitnessStepPolicySkip`.
 *
 * Adding a key here covers all three paths that must agree: it seeds every
 * closing step, `cancelProceeding` skips it, and `revertCanceledProceeding`
 * restores it to its policy default. Skipping on cancel without restoring on
 * revert leaves the action permanently skipped, so both sides read this list.
 */
export const NORMAL_CLOSING_ACTIONS = [
  'clarify_exhibit',
  'remind_exhibit',
  'clarify_spelling',
  'confirm_read_or_waive',
  'select_errata_recipient',
  'collect_transcript_orders',
  'confirm_orders',
  'confirm_stipulations',
  'go_off_the_record',
] as const

export const ATTENDEES_HAVE_LEFT_CALL_ACTION_KEY = 'attendees_have_left_call'

/**
 * Closing actions that only apply to a cancelled proceeding. They are skipped
 * by default, and re-skipped when a cancellation is reverted.
 */
export const CANCELLATION_CLOSING_ACTIONS = [
  'declare_how_deposition_ended',
  'state_the_cna',
  'select_reason_for_cna',
  'no_show_go_on_the_record',
  'no_show_script',
] as const

/**
 * The cancellation actions the reporter must actually complete, so cancelling
 * unskips them. The rest stay skipped until a reason is selected.
 */
export const CLOSING_ACTIONS_UNSKIPPED_ON_CANCELLATION = [
  'declare_how_deposition_ended',
] as const

const closingActionKeys = (
  keys: readonly string[],
): { step: ScriptStep; key: string }[] =>
  keys.map(key => ({ step: ScriptStep.CLOSING, key }))

/** Skip value each normal closing action returns to outside a cancellation. */
const normalClosingActionDefaults = (policies: WitnessStepPolicies) =>
  NORMAL_CLOSING_ACTIONS.map(key => ({
    key,
    // A policy-skipped action is hidden in the UI (e.g. confirm_read_or_waive in
    // MI, WI, NY), so unskipping it would block canContinue forever.
    skip: getWitnessStepPolicySkip(ScriptStep.CLOSING, key, policies) ?? false,
  }))

const getDefaultWitnessSteps = (witnessIndex: number) => ({
  [formatStepKey(ScriptStep.ATTENDANCE, witnessIndex)]: createWitnessStep(
    ScriptStep.ATTENDANCE,
    {
      attendees: createAction('attendees'),
      cna: createAction('cna'),
    },
  ),
  [formatStepKey(ScriptStep.OPENING, witnessIndex)]: createWitnessStep(
    ScriptStep.OPENING,
    {
      spotlight_the_wittness: createAction('spotlight_the_wittness'),
      go_on_the_record: createAction('go_on_the_record'),
      identify_counsels: createAction('identify_counsels'),
      let_counsels_stipulate: createAction('let_counsels_stipulate'),
      identify_wittness: createAction('identify_wittness'),
      swear_in_witness: createAction('swear_in_witness'),
      identify_interpreter: createAction('identify_interpreter', {
        optional: true,
      }),
      identify_guardian: createAction('identify_guardian', { optional: true }),
      other_parties_identification: createAction(
        'other_parties_identification',
      ),
    },
  ),
  [formatStepKey(ScriptStep.QUESTIONING, witnessIndex)]: createWitnessStep(
    ScriptStep.QUESTIONING,
    {
      start_questioning: createAction('start_questioning'),
      oversight_the_progress: createAction('oversight_the_progress'),
    },
  ),
  [formatStepKey(ScriptStep.CLOSING, witnessIndex)]: createWitnessStep(
    ScriptStep.CLOSING,
    {
      ...Object.fromEntries(
        NORMAL_CLOSING_ACTIONS.map(key => [key, createAction(key)]),
      ),
      [ATTENDEES_HAVE_LEFT_CALL_ACTION_KEY]: createAction(
        ATTENDEES_HAVE_LEFT_CALL_ACTION_KEY,
      ),
      ...Object.fromEntries(
        CANCELLATION_CLOSING_ACTIONS.map(key => [
          key,
          createAction(key, { skip: true }),
        ]),
      ),
    },
  ),
})

const buildWitnessStepForIndex = (
  stepKey: ScriptStep,
  targetWitnessIndex: number,
  policies: WitnessStepPolicies,
) => {
  const defaultStep =
    getDefaultWitnessSteps(targetWitnessIndex)[
      formatStepKey(stepKey, targetWitnessIndex)
    ]

  return {
    ...defaultStep,
    status: ScriptStepStatus.INCOMPLETE,
    actions: Object.fromEntries(
      Object.entries(defaultStep.actions).map(([key, action]) => {
        const policySkip = getWitnessStepPolicySkip(stepKey, key, policies)

        return [
          key,
          {
            ...action,
            completed: false,
            ...(policySkip !== undefined && {
              skip: policySkip,
            }),
          },
        ]
      }),
    ),
  }
}

export const buildWitnessStepsForIndex = (
  witnessIndex: number,
  policies: WitnessStepPolicies,
) => {
  return Object.fromEntries(
    witnessScopedSteps.map(stepKey => [
      formatStepKey(stepKey, witnessIndex),
      buildWitnessStepForIndex(stepKey, witnessIndex, policies),
    ]),
  )
}

const putReviewStepLast = (
  steps: DRScriptData['steps'],
): DRScriptData['steps'] => {
  const reviewStep = steps[ScriptStep.REVIEW]
  const stepsWithoutReview = Object.fromEntries(
    Object.entries(steps).filter(([key]) => key !== ScriptStep.REVIEW),
  )

  if (reviewStep === undefined) {
    return stepsWithoutReview
  }

  return {
    ...stepsWithoutReview,
    [ScriptStep.REVIEW]: reviewStep,
  }
}

export const buildWitnessEventAttendees = (
  attendees: ScriptAttendee[],
  activeWitnessId: string,
): ScriptAttendee[] =>
  attendees.map(attendee => ({
    ...attendee,
    role:
      attendee.role === AttendeeRole.WITNESS &&
      attendee.attendee_id !== activeWitnessId
        ? AttendeeRole.OTHER
        : attendee.role,
  }))

const isBlankNewAttendeePlaceholder = (attendee: ScriptAttendee): boolean =>
  (attendee.role as AttendeeRole | null) === null &&
  (attendee.name ?? '').trim() === '' &&
  (attendee.email ?? '').trim() === '' &&
  attendee.present === null &&
  (attendee.zoom_participants ?? []).length === 0 &&
  attendee.identity_document === null &&
  (attendee.city ?? null) === null &&
  (attendee.state ?? null) === null

const createWitnessEvent = (
  attendees: ScriptAttendee[],
  activeWitnessId: string,
): EventScriptDataObject =>
  ({
    current_step: null,
    first_skipped_step: null,
    attendees: buildWitnessEventAttendees(attendees, activeWitnessId),
    zoom_participants: [],
    low_confidence_words: null,
    isRecordingStarted: undefined,
    last_timestamp: null,
    is_on_record: false,
    timestamps: null,
    spellings: null,
    notes: null,
    witness_requested_transcript: null,
    read_waive_answer: 'no_answer',
    errata_recipient_attendee_id: null,
    order_transcript: false,
    isRecordingEnded: undefined,
    cna_declared_at: null,
    reason_for_cna: undefined,
    other_reason: '',
    submitted: false,
    shown_exhibits: null,
    exhibits: null,
    confirmed_no_exhibits: null,
    result: null as never as DepositionResult,
  }) as EventScriptDataObject

const witnessScopedSteps = [
  ScriptStep.ATTENDANCE,
  ScriptStep.OPENING,
  ScriptStep.QUESTIONING,
  ScriptStep.CLOSING,
]

const getCurrentStepAfterWitnessAppend = (state: DRScriptData): string => {
  const { step, witnessIndex } = parseStepKey(state.current_step)

  if (
    state.events.length === 1 &&
    witnessIndex === undefined &&
    witnessScopedSteps.includes(step)
  ) {
    return formatStepKey(step, 0)
  }

  return state.current_step
}

const isWitnessScopedStepKey = (key: string) => {
  const { step, witnessIndex } = parseStepKey(key)
  return witnessIndex !== undefined && witnessScopedSteps.includes(step)
}

const getWitnessIdForEvent = (
  event: EventScriptDataObject,
): string | undefined =>
  event.attendees.find(attendee => attendee.role === AttendeeRole.WITNESS)
    ?.attendee_id

const hasSliceProgress = (
  state: DRScriptData,
  witnessIndex: number,
): boolean => {
  const event = state.events[witnessIndex]

  if (!event) {
    return true
  }

  // Landing on a step is not progress: the script starts the next witness's
  // attendance step by itself, before the reporter can spot a wrong witness.
  const hasStepProgress = witnessScopedSteps.some(stepKey => {
    const step = state.steps[formatStepKey(stepKey, witnessIndex)]
    if (step?.status === undefined) {
      return false
    }

    if (
      step.status !== ScriptStepStatus.INCOMPLETE &&
      step.status !== ScriptStepStatus.IN_PROGRESS
    ) {
      return true
    }

    return Object.values(step.actions ?? {}).some(action => action.completed)
  })

  return (
    hasStepProgress ||
    event.current_step !== null ||
    event.first_skipped_step !== null ||
    event.low_confidence_words !== null ||
    event.last_timestamp !== null ||
    event.cna_declared_at !== null ||
    event.is_on_record ||
    event.timestamps !== null ||
    event.spellings !== null ||
    event.read_waive_answer !== 'no_answer' ||
    event.order_transcript === true ||
    event.notes !== null ||
    (event.reason_for_cna ?? null) !== null ||
    (event.other_reason ?? '') !== '' ||
    event.result !== null ||
    event.submitted === true
  )
}

export const canRemoveWitnessSlice = (
  state: DRScriptData,
  witnessIndex: number,
): boolean =>
  state.events.length > 1 &&
  witnessIndex > 0 &&
  witnessIndex < state.events.length &&
  !hasSliceProgress(state, witnessIndex)

const reindexStepKeyAfterRemoval = (
  key: string,
  removedWitnessIndex: number,
): string => {
  const { step, witnessIndex } = parseStepKey(key)
  if (witnessIndex === undefined) {
    return key
  }

  const parsedWitnessIndex = Number(witnessIndex)
  if (Number.isNaN(parsedWitnessIndex)) {
    return key
  }

  if (parsedWitnessIndex < removedWitnessIndex) {
    return key
  }

  return formatStepKey(step, parsedWitnessIndex - 1)
}

const isStepFinished = (step: ScriptDataStep | undefined): boolean =>
  step === undefined ||
  step.status === ScriptStepStatus.COMPLETED ||
  step.status === ScriptStepStatus.SKIPPED

// Continuing from the final witness's closing is what ends the call, so a
// closing finished while another witness followed still owes that step once
// its witness becomes the last one.
const reopenClosingOwingCallEnd = (
  steps: DRScriptData['steps'],
  witnessCount: number,
): DRScriptData['steps'] => {
  const closingStepKey = formatStepKey(ScriptStep.CLOSING, witnessCount - 1)
  const closingStep = steps[closingStepKey]
  const endCallAction =
    closingStep?.actions[ATTENDEES_HAVE_LEFT_CALL_ACTION_KEY]

  if (
    !isStepFinished(closingStep) ||
    endCallAction === undefined ||
    endCallAction.completed ||
    endCallAction.skip === true
  ) {
    return steps
  }

  return {
    ...steps,
    [closingStepKey]: {
      ...closingStep,
      status: ScriptStepStatus.IN_PROGRESS,
    },
  }
}

const getFirstUnfinishedStep = (
  steps: DRScriptData['steps'],
  witnessCount: number,
): string | undefined =>
  Array.from({ length: witnessCount }, (_, witnessIndex) =>
    witnessScopedSteps
      .map(stepKey => formatStepKey(stepKey, witnessIndex))
      .filter(stepKey => !isStepFinished(steps[stepKey])),
  ).flat()[0]

const getCurrentStepAfterWitnessRemoval = (
  state: DRScriptData,
  nextSteps: DRScriptData['steps'],
  remainingWitnessCount: number,
  removedWitnessIndex: number,
): string => {
  const { witnessIndex } = parseStepKey(state.current_step)
  if (witnessIndex === undefined) {
    return state.current_step
  }

  const parsedWitnessIndex = Number(witnessIndex)
  if (Number.isNaN(parsedWitnessIndex)) {
    return state.current_step
  }

  if (parsedWitnessIndex !== removedWitnessIndex) {
    return reindexStepKeyAfterRemoval(state.current_step, removedWitnessIndex)
  }

  // The slice the reporter stood in is gone, so pick up the earliest step the
  // script still needs -- which may belong to a later witness.
  return (
    getFirstUnfinishedStep(nextSteps, remainingWitnessCount) ??
    (nextSteps[ScriptStep.REVIEW] !== undefined
      ? ScriptStep.REVIEW
      : formatStepKey(
          ScriptStep.CLOSING,
          Math.max(0, remainingWitnessCount - 1),
        ))
  )
}

const getStepsAfterWitnessRemoval = (
  state: DRScriptData,
  removedWitnessIndex: number,
) => {
  const nextSteps = Object.fromEntries(
    Object.entries(state.steps).filter(([key]) => !isWitnessScopedStepKey(key)),
  )

  state.events.forEach((_, oldWitnessIndex) => {
    if (oldWitnessIndex === removedWitnessIndex) {
      return
    }

    const newWitnessIndex =
      oldWitnessIndex > removedWitnessIndex
        ? oldWitnessIndex - 1
        : oldWitnessIndex

    witnessScopedSteps.forEach(stepKey => {
      const oldKey = formatStepKey(stepKey, oldWitnessIndex)
      const step = state.steps[oldKey]
      if (step !== undefined) {
        nextSteps[formatStepKey(stepKey, newWitnessIndex)] = step
      }
    })
  })

  const reviewStep = state.steps[ScriptStep.REVIEW]
  if (reviewStep !== undefined) {
    nextSteps[ScriptStep.REVIEW] = {
      ...reviewStep,
      actions: Object.fromEntries(
        Object.entries(reviewStep.actions).flatMap(([key, action]) => {
          if (!key.startsWith('review-')) {
            return [[key, action]]
          }

          const reviewIndex = Number(key.replace('review-', ''))
          if (
            Number.isNaN(reviewIndex) ||
            reviewIndex === removedWitnessIndex
          ) {
            return []
          }

          const nextReviewIndex =
            reviewIndex > removedWitnessIndex ? reviewIndex - 1 : reviewIndex
          const nextKey = `review-${nextReviewIndex}`
          return [[nextKey, { ...action, key: nextKey }]]
        }),
      ),
    }
  }

  return putReviewStepLast(nextSteps)
}

export const appendWitnessSlice = (
  state: DRScriptData,
  witness: ScriptAttendee,
  policies: WitnessStepPolicies,
): DRScriptData => {
  const witnessIndex = state.events.length
  const { currentEventIndex } = getCurrentEventState(state)
  const firstEventIndexForNewWitness = currentEventIndex ?? 0
  const sourceAttendees = state.attendees.filter(
    attendee => !isBlankNewAttendeePlaceholder(attendee),
  )
  const attendees = sourceAttendees.some(
    attendee => attendee.attendee_id === witness.attendee_id,
  )
    ? sourceAttendees.map(attendee =>
        attendee.attendee_id === witness.attendee_id ? witness : attendee,
      )
    : [...sourceAttendees, witness]

  return {
    ...state,
    attendees,
    current_step: getCurrentStepAfterWitnessAppend(state),
    steps: putReviewStepLast({
      ...state.steps,
      ...buildWitnessStepsForIndex(witnessIndex, policies),
      ...(state.steps[ScriptStep.REVIEW] !== undefined && {
        [ScriptStep.REVIEW]: {
          ...state.steps[ScriptStep.REVIEW],
          actions: {
            ...state.steps[ScriptStep.REVIEW].actions,
            [`review-${witnessIndex}`]: {
              key: `review-${witnessIndex}`,
              completed: false,
              skip: false,
            },
          },
        },
      }),
    }),
    events: [
      ...state.events.map((event, index) => {
        // Don't update the attendees in previous witness sections
        if (index < firstEventIndexForNewWitness) {
          return event
        }

        return {
          ...event,
          attendees: buildWitnessEventAttendees(
            attendees,
            event.attendees.find(
              attendee => attendee.role === AttendeeRole.WITNESS,
            )?.attendee_id ?? witness.attendee_id,
          ),
        }
      }),
      createWitnessEvent(attendees, witness.attendee_id),
    ],
  }
}

export const removeUnstartedWitnessSlice = (
  state: DRScriptData,
  witnessIndex: number,
): DRScriptData => {
  if (!canRemoveWitnessSlice(state, witnessIndex)) {
    return state
  }

  const removedWitnessId = getWitnessIdForEvent(state.events[witnessIndex])
  const attendees =
    removedWitnessId === undefined
      ? state.attendees
      : state.attendees.filter(
          attendee => attendee.attendee_id !== removedWitnessId,
        )
  const { currentEventIndex } = getCurrentEventState(state)
  const firstEventIndexForRemoval = currentEventIndex ?? 0
  const remainingEvents = state.events.flatMap((event, index) => {
    if (index === witnessIndex) {
      return []
    }

    if (index < firstEventIndexForRemoval) {
      return [event]
    }

    const activeWitnessId = getWitnessIdForEvent(event)
    return [
      {
        ...event,
        attendees:
          activeWitnessId === undefined
            ? event.attendees
            : buildWitnessEventAttendees(attendees, activeWitnessId),
      },
    ]
  })

  const nextSteps = reopenClosingOwingCallEnd(
    getStepsAfterWitnessRemoval(state, witnessIndex),
    remainingEvents.length,
  )
  const nextCurrentStep = getCurrentStepAfterWitnessRemoval(
    state,
    nextSteps,
    remainingEvents.length,
    witnessIndex,
  )
  const stateAfterRemoval = {
    ...state,
    attendees,
    current_step: nextCurrentStep,
    steps: nextSteps,
    events: remainingEvents,
  } as DRScriptData

  // The timeline disables incomplete steps, so the one we land on has to be
  // started or the reporter cannot navigate back to it.
  return nextSteps[nextCurrentStep]?.status === ScriptStepStatus.INCOMPLETE
    ? startStep(stateAfterRemoval, nextCurrentStep)
    : stateAfterRemoval
}

export const updateAttendee = (
  state: DRScriptData,
  attendee: ScriptAttendee,
) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)

  if (hasEventIndex) {
    const events = (state as ScriptData).events.map((event, index) => {
      if (index === currentEventIndex) {
        return {
          ...event,
          attendees: event.attendees.map(a =>
            a.attendee_id === attendee.attendee_id
              ? sanitizeAttendeeIdentityDocument({ ...a, ...attendee })
              : a,
          ),
        }
      }

      if (index > Number(currentEventIndex)) {
        return {
          ...event,
          attendees: event.attendees.map(a =>
            a.attendee_id === attendee.attendee_id
              ? { ...a, name: attendee.name, email: attendee.email }
              : a,
          ),
        }
      }
      return event
    })
    const masterAttendeesList = _getMasterAttendeesList(
      events,
      currentEventIndex,
    )

    return {
      ...state,
      attendees: masterAttendeesList,
      events,
    }
  }

  const updatedAttendees = state.attendees.map(a =>
    a.attendee_id === attendee.attendee_id
      ? sanitizeAttendeeIdentityDocument({ ...a, ...attendee })
      : a,
  )

  return {
    ...state,
    attendees: [...updatedAttendees],
  }
}

export const updateAttendees = (
  state: DRScriptData,
  input: UpdateAttendeesInput,
) => {
  const { attendees, preserveExistingZoomParticipants = false } = input
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)
  const attendeesForMerge = hasEventIndex
    ? (state as ScriptData).events[Number(currentEventIndex)]?.attendees ??
      state.attendees
    : state.attendees
  const updatedAttendees: ScriptAttendee[] = attendees.map(a => {
    const existingAttendee = attendeesForMerge.find(
      attendee => attendee.attendee_id === a.attendee_id,
    )
    const zoomParticipants = preserveExistingZoomParticipants
      ? mergeZoomParticipants(
          existingAttendee?.zoom_participants,
          a.zoom_participants,
        )
      : a.zoom_participants ?? existingAttendee?.zoom_participants ?? []

    return sanitizeAttendeeIdentityDocument({
      ...existingAttendee,
      ...a,
      zoom_participants: zoomParticipants,
    })
  })

  if (hasEventIndex) {
    const currentIndex = Number(currentEventIndex)
    const scriptEvents = (state as ScriptData).events
    const removedAttendeeIds = new Set(
      (scriptEvents[currentIndex]?.attendees ?? [])
        .filter(
          attendee =>
            !updatedAttendees.some(
              updatedAttendee =>
                updatedAttendee.attendee_id === attendee.attendee_id,
            ),
        )
        .map(attendee => attendee.attendee_id),
    )
    const events = scriptEvents.map((event, index) => {
      if (index === currentIndex) {
        return {
          ...event,
          attendees: updatedAttendees,
        }
      }

      if (index > currentIndex) {
        const existingAttendees = !hasSliceProgress(state, index)
          ? event.attendees.filter(
              attendee => !removedAttendeeIds.has(attendee.attendee_id),
            )
          : event.attendees
        const addedAttendees = updatedAttendees.filter(
          a => !existingAttendees.some(e => e.attendee_id === a.attendee_id),
        )
        const updatedAttendeesById = new Map(
          updatedAttendees.map(attendee => [attendee.attendee_id, attendee]),
        )
        const upcomingEventAttendees = [...existingAttendees, ...addedAttendees]
        return {
          ...event,
          attendees: upcomingEventAttendees.map(a => {
            const updatedAttendee = updatedAttendeesById.get(a.attendee_id)

            return {
              ...a,
              name: updatedAttendee?.name ?? a.name,
              email: updatedAttendee?.email ?? a.email,
              present: updatedAttendee?.present ?? a.present,
              zoom_participants:
                updatedAttendee?.zoom_participants ?? a.zoom_participants,
            }
          }),
        }
      }

      return event
    })
    const masterAttendeesList = _getMasterAttendeesList(
      events,
      currentIndex,
    ).filter(attendee => !removedAttendeeIds.has(attendee.attendee_id))

    return {
      ...state,
      attendees: masterAttendeesList,
      events,
    }
  }

  return {
    ...state,
    attendees: [...updatedAttendees],
  }
}

/**
 * Combined action that updates attendees and completes an action in a single state mutation.
 * This prevents race conditions where sequential actions using debounced uploads
 * would overwrite each other due to using the same base state.
 */
export const updateAttendeesAndCompleteAction = (
  state: DRScriptData,
  {
    attendees,
    preserveExistingZoomParticipants,
    step,
    key,
  }: UpdateAttendeesInput & { step: string; key: string },
) => {
  // First apply updateAttendees
  const stateWithAttendees = updateAttendees(state, {
    attendees,
    preserveExistingZoomParticipants,
  })
  // Then apply completeStepAction on the result
  return completeStepAction(stateWithAttendees, { step, key })
}

// Questioning
export const updateSpellings = (state: DRScriptData, spellings: string) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)

  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === currentEventIndex ? { ...event, spellings } : event,
      ),
    }
  }

  return {
    ...state,
    spellings,
  }
}

export const updateConductingData = (
  state: DRScriptData,
  {
    notes,
    spellings,
    timestamps,
  }: {
    notes: string
    spellings: string
    timestamps: string
  },
) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)

  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex)
          ? { ...event, spellings, timestamps, notes }
          : event,
      ),
    }
  }

  return {
    ...state,
    notes,
    spellings,
    timestamps,
  }
}

// Closing
export const updateOrderTranscript = (
  state: DRScriptData,
  orderTranscript: boolean,
) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)

  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex)
          ? {
              ...event,
              order_transcript: orderTranscript,
              ...(!orderTranscript && {
                attendees: event.attendees?.map(a => ({
                  ...a,
                  transcript_order: null,
                })),
              }),
            }
          : event,
      ),
    }
  }

  return {
    ...state,
    order_transcript: orderTranscript,
    ...(!orderTranscript && {
      attendees: state.attendees?.map(a => ({ ...a, transcript_order: null })),
    }),
  }
}

// Only the answer is stored, and the legacy boolean is cleared with it. Parrot
// derives that boolean when the feedback is built, while the backfill on load
// treats one left behind by a pre-tri-state script as the answer to restore --
// so leaving it would let an old answer overwrite this one.
export const withReadWaiveAnswer = (
  event: EventScriptDataObject,
  readWaiveAnswer: ReadWaiveAnswer,
): EventScriptDataObject => ({
  ...event,
  read_waive_answer: readWaiveAnswer,
  witness_requested_transcript: null,
})

export const updateReadWaiveAnswer = (
  state: DRScriptData,
  readWaiveAnswer: ReadWaiveAnswer,
) => {
  const witnessRequestedTranscript = readWaiveAnswer === 'read'
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)

  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex)
          ? {
              ...withReadWaiveAnswer(event, readWaiveAnswer),
              ...(!witnessRequestedTranscript && {
                errata_recipient_attendee_id: null,
              }),
            }
          : event,
      ),
    }
  }

  // The unindexed shape gets the same treatment: the answer is stored and the
  // legacy boolean cleared with it.
  return {
    ...state,
    read_waive_answer: readWaiveAnswer,
    witness_requested_transcript: null,
    ...(!witnessRequestedTranscript && {
      errata_recipient_attendee_id: null,
    }),
  }
}

export const updateErrataRecipient = (
  state: DRScriptData,
  errataRecipientAttendeeId: string | null,
) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)

  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex)
          ? {
              ...event,
              errata_recipient_attendee_id: errataRecipientAttendeeId,
            }
          : event,
      ),
    }
  }

  return {
    ...state,
    errata_recipient_attendee_id: errataRecipientAttendeeId,
  }
}

export const setFirstSkippedStep = (
  state: DRScriptData,
  step: string | null,
  currentEventIndex?: number,
) => {
  if (currentEventIndex !== undefined) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === currentEventIndex
          ? { ...event, first_skipped_step: step }
          : event,
      ),
    }
  }

  return {
    ...state,
    first_skipped_step: step,
  }
}

export const updateEventResult = (
  state: DRScriptData,
  result: DepositionResult,
) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)

  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex) ? { ...event, result } : event,
      ),
    }
  }

  return {
    ...state,
    result,
  }
}

export const updateNotes = (state: DRScriptData, notes: string) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)

  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex) ? { ...event, notes } : event,
      ),
    }
  }

  return {
    ...state,
    notes: notes,
  }
}

// Exhibits (DR Exhibits). `null` means "not collected"; the section being
// shown to the reporter moves it to `[]` / `false` (see ensureExhibitsTracked).
const updateCurrentWitnessEvent = (
  state: DRScriptData,
  updater: (event: EventScriptDataObject) => EventScriptDataObject,
): DRScriptData => {
  const { currentEventIndex } = getCurrentEventState(state)
  return _updateCurrentEvent(state, updater, currentEventIndex)
}

// Starts at the next position and skips numbers already taken, so deleting a
// middle exhibit never makes the next row repeat an existing number.
const getNextExhibitNumber = (exhibits: ScriptExhibit[]) => {
  const taken = new Set(exhibits.map(exhibit => exhibit.exhibit_number))
  let position = exhibits.length + 1

  while (taken.has(`Exhibit ${position}`)) {
    position += 1
  }
  return `Exhibit ${position}`
}

// The list stays in marked-at order. The sort is stable, so exhibits marked at
// the same second keep their relative order.
const sortByMarkedAt = (exhibits: ScriptExhibit[]) =>
  [...exhibits].sort(
    (a, b) => Date.parse(a.marked_at) - Date.parse(b.marked_at),
  )

export type UpdateExhibitInput = {
  id: string
  patch: Partial<
    Pick<
      ScriptExhibit,
      'exhibit_number' | 'description' | 'designation' | 'marked_at'
    >
  >
}

export const addExhibit = (state: DRScriptData) =>
  updateCurrentWitnessEvent(state, event => {
    const exhibits = event.exhibits ?? []

    return {
      ...event,
      // An earlier exhibit may have been moved to a later time, so the new row
      // is not always last.
      exhibits: sortByMarkedAt([
        ...exhibits,
        {
          id: uuid(),
          exhibit_number: getNextExhibitNumber(exhibits),
          description: '',
          designation: 'unknown',
          marked_at: new Date().toISOString(),
        },
      ]),
      confirmed_no_exhibits: false,
    }
  })

export const updateExhibit = (
  state: DRScriptData,
  { id, patch }: UpdateExhibitInput,
) =>
  updateCurrentWitnessEvent(state, event => {
    const exhibits = (event.exhibits ?? []).map(exhibit => {
      if (exhibit.id !== id) {
        return exhibit
      }

      const markedAtIsValid =
        patch.marked_at !== undefined &&
        !Number.isNaN(Date.parse(patch.marked_at))

      // The id never changes, and neither does a marked_at that is not a time.
      return {
        ...exhibit,
        ...patch,
        id: exhibit.id,
        marked_at: markedAtIsValid ? patch.marked_at! : exhibit.marked_at,
      }
    })

    return {
      ...event,
      exhibits:
        patch.marked_at === undefined ? exhibits : sortByMarkedAt(exhibits),
    }
  })

// Deleting does not renumber: the reporter may have typed custom numbers.
// Emptying the list answers the question with "no exhibits", so it never goes
// back to the initial unanswered state.
export const deleteExhibit = (state: DRScriptData, id: string) =>
  updateCurrentWitnessEvent(state, event => {
    const exhibits = (event.exhibits ?? []).filter(exhibit => exhibit.id !== id)

    return {
      ...event,
      exhibits,
      confirmed_no_exhibits:
        exhibits.length === 0 ? true : event.confirmed_no_exhibits,
    }
  })

export const setNoExhibits = (state: DRScriptData, noExhibits: boolean) =>
  updateCurrentWitnessEvent(state, event =>
    // "No exhibits" contradicts a non-empty list.
    noExhibits && (event.exhibits ?? []).length > 0
      ? event
      : { ...event, confirmed_no_exhibits: noExhibits },
  )

// Idempotent: returns the same state once the current witness is tracked, so
// callers can dispatch it freely without causing a redundant save.
export const ensureExhibitsTracked = (state: DRScriptData) => {
  const { currentEventIndex } = getCurrentEventState(state)
  const event = state.events[currentEventIndex ?? 0]

  if (
    !event ||
    ((event.exhibits ?? null) !== null &&
      (event.confirmed_no_exhibits ?? null) !== null)
  ) {
    return state
  }

  return _updateCurrentEvent(
    state,
    current => ({
      ...current,
      exhibits: current.exhibits ?? [],
      confirmed_no_exhibits: current.confirmed_no_exhibits ?? false,
    }),
    currentEventIndex,
  )
}

export const updateReasonForCna = (
  state: DRScriptData,
  reasonForCna: ReasonForCna | undefined,
) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)

  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex)
          ? { ...event, reason_for_cna: reasonForCna }
          : event,
      ),
    }
  }

  return {
    ...state,
    reason_for_cna: reasonForCna,
  }
}

export const updateOtherReason = (state: DRScriptData, otherReason: string) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)

  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex)
          ? { ...event, other_reason: otherReason }
          : event,
      ),
    }
  }

  return {
    ...state,
    other_reason: otherReason,
  }
}

// Review
export const updateState = (newState: DRScriptData, state: string) => {
  return {
    ...newState,
    reporter_state: state,
  }
}

export const updateCounty = (state: DRScriptData, county: string) => {
  return {
    ...state,
    reporter_county: county,
  }
}

export const updateLowConfidenceWords = (
  state: DRScriptData,
  words: LowConfidenceWord[],
) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)

  if (hasEventIndex) {
    return {
      ...state,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex)
          ? { ...event, low_confidence_words: words }
          : event,
      ),
    }
  }

  return {
    ...state,
    low_confidence_words: words,
  }
}

// Script timeline
export const completeStepAction = (
  state: DRScriptData,
  {
    result,
    step,
    key,
  }: { result?: DepositionResult; step: string; key: string },
) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)
  const steps = {
    ...state.steps,
    [step]: {
      ...state.steps[step],
      actions: {
        ...state.steps[step].actions,
        [key]: {
          ...state.steps[step].actions[key],
          key,
          completed: true,
        },
      },
    },
  }

  if (hasEventIndex) {
    return {
      ...state,
      steps: steps,
      events: (state as ScriptData).events.map((event, index) =>
        index === Number(currentEventIndex)
          ? { ...event, ...(result && { result }) }
          : event,
      ),
    } as DRScriptData
  }

  return {
    ...state,
    ...(result && { result }),
    steps: steps,
  } as DRScriptData
}

/**
 * Returns a copy of `steps` with `skip` set on each target action.
 *
 * Actions missing from the persisted script are left alone rather than throwing:
 * script data is stored as a JSON blob, so an event created under an older
 * schema can lack a closing action that was added later (DEPALER-738).
 *
 * A swallowed write leaves a breadcrumb because the two directions fail very
 * differently. An ignored skip leaves the action active, which the reporter sees
 * immediately as a closing step that will not complete. An ignored unskip leaves
 * it skipped, so the step completes *without* the action and nothing surfaces —
 * that is how the missing `confirm_stipulations` unskip went unnoticed. Neither
 * is worth an exception mid-deposition, but the second needs a trail.
 */
const withActionSkip = (
  steps: DRScriptData['steps'],
  stepAndActionKeys: StepAndActionKey[],
  { currentEventIndex, skip }: { currentEventIndex?: number; skip: boolean },
): DRScriptData['steps'] =>
  stepAndActionKeys.reduce((acc, { step, key }) => {
    const stepKey = formatStepKey(step as ScriptStep, currentEventIndex)
    const targetStep = acc[stepKey]
    const action = targetStep?.actions?.[key]

    if (action === undefined) {
      Sentry.addBreadcrumb({
        category: 'script-state',
        level: skip ? 'info' : 'warning',
        message: `Ignored ${skip ? 'skip' : 'unskip'} of missing action "${key}" on step "${stepKey}"`,
        data: {
          step: stepKey,
          action: key,
          direction: skip ? 'skip' : 'unskip',
        },
      })

      return acc
    }

    return {
      ...acc,
      [stepKey]: {
        ...targetStep,
        actions: {
          ...targetStep.actions,
          [key]: { ...action, skip },
        },
      },
    }
  }, steps)

export const skipStepAction = (
  state: DRScriptData,
  {
    stepAndActionKeys,
    result,
    reasonForCNA,
    otherReason,
    notes,
    currentEventIndex,
  }: {
    stepAndActionKeys: StepAndActionKey[]
    result?: DepositionResult
    reasonForCNA?: ReasonForCna
    otherReason?: string
    notes?: string
    currentEventIndex?: number
  },
) => {
  const steps = withActionSkip(state.steps, stepAndActionKeys, {
    currentEventIndex,
    skip: true,
  })

  if (currentEventIndex !== undefined) {
    return {
      ...state,
      steps: {
        ...state.steps,
        ...steps,
      },
      events: (state as ScriptData).events.map((event, index) =>
        index === currentEventIndex
          ? {
              ...event,
              ...(result && { result }),
              ...(reasonForCNA && { reason_for_cna: reasonForCNA }),
              ...(otherReason && { other_reason: otherReason }),
              ...(notes && { notes }),
            }
          : event,
      ),
    } as DRScriptData
  }

  return {
    ...state,
    ...(result && { result }),
    ...(reasonForCNA && { reason_for_cna: reasonForCNA }),
    ...(otherReason && { other_reason: otherReason }),
    ...(notes && { notes }),
    steps: {
      ...state.steps,
      ...steps,
    },
  } as DRScriptData
}

export const unskipStepAction = (
  state: DRScriptData,
  {
    stepAndActionKeys,
    result,
    reasonForCNA,
    otherReason,
    notes,
    currentEventIndex,
  }: {
    stepAndActionKeys: StepAndActionKey[]
    result?: DepositionResult
    reasonForCNA?: ReasonForCna
    otherReason?: string
    notes?: string
    currentEventIndex?: number
  },
) => {
  const steps = withActionSkip(state.steps, stepAndActionKeys, {
    currentEventIndex,
    skip: false,
  })

  if (currentEventIndex !== undefined) {
    return {
      ...state,
      steps: {
        ...state.steps,
        ...steps,
      },
      events: (state as ScriptData).events.map((event, index) =>
        index === currentEventIndex
          ? {
              ...event,
              ...(result && { result }),
              ...(reasonForCNA && { reason_for_cna: reasonForCNA }),
              ...(otherReason && { other_reason: otherReason }),
              ...(notes && { notes }),
            }
          : event,
      ),
    } as DRScriptData
  }

  return {
    ...state,
    ...(result && { result }),
    ...(reasonForCNA && { reason_for_cna: reasonForCNA }),
    ...(otherReason && { other_reason: otherReason }),
    ...(notes && { notes }),
    steps: {
      ...state.steps,
      ...steps,
    },
  } as DRScriptData
}

export const undoStepAction = (
  state: DRScriptData,
  { step, key }: { step: string; key: string },
) => {
  return {
    ...state,
    steps: {
      ...state.steps,
      [step]: {
        ...state.steps[step],
        actions: {
          ...state.steps[step].actions,
          [key]: {
            ...state.steps[step].actions[key],
            completed: false,
          },
        },
      },
    },
  } as DRScriptData
}

export const updateCurrentStep = (
  state: DRScriptData,
  current_step: ScriptStep | string,
) => {
  return {
    ...state,
    current_step,
  } as DRScriptData
}

export const completeStep = (
  state: DRScriptData,
  step: ScriptStep | string,
) => {
  return {
    ...state,
    steps: {
      ...state.steps,
      [step]: {
        ...state.steps[step],
        status: ScriptStepStatus.COMPLETED,
      },
    },
  } as DRScriptData
}

export const completeStepAndContinue = (
  state: DRScriptData,
  {
    step,
    nextStep,
  }: { step: ScriptStep | string; nextStep: ScriptStep | string },
) => {
  const result = startStep(
    completeStep(state, step) as DRScriptData,
    nextStep,
  ) as DRScriptData
  return updateCurrentStep(result, nextStep)
}

export const skipStep = (state: DRScriptData, step: ScriptStep | string) => {
  return {
    ...state,
    steps: {
      ...state.steps,
      [step]: {
        ...state.steps[step],
        status: ScriptStepStatus.SKIPPED,
      },
    },
  } as DRScriptData
}

export const incompleteStep = (state: DRScriptData, step: ScriptStep) => {
  return {
    ...state,
    steps: {
      ...state.steps,
      [step]: {
        ...state.steps[step],
        status: ScriptStepStatus.INCOMPLETE,
      },
    },
  } as DRScriptData
}

export const startStep = (state: DRScriptData, step: ScriptStep | string) => {
  return {
    ...state,
    steps: {
      ...state.steps,
      [step]: {
        ...state.steps[step],
        status: ScriptStepStatus.IN_PROGRESS,
      },
    },
  } as DRScriptData
}

export const revertCanceledProceeding = (
  state: DRScriptData,
  policies: WitnessStepPolicies,
) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)
  const currentEvent = (state as ScriptData).events[currentEventIndex ?? 0]
  const targetStep = currentEvent?.first_skipped_step

  if (targetStep === null) {
    return state
  }

  const closingSteps = [ScriptStep.CLOSING, ScriptStep.REVIEW]
  let newState = { ...state }

  // Unskip all steps
  Object.entries(state.steps).forEach(([key, step]) => {
    if (
      parseStepKey(key).witnessIndex === currentEventIndex?.toString() &&
      (step.status === ScriptStepStatus.SKIPPED ||
        closingSteps.includes(step.key as ScriptStep))
    ) {
      newState = incompleteStep(newState, key as ScriptStep)
    }
  })

  // Restore every normal closing action to its policy default, and re-skip the
  // cancellation-only ones. Both lists are derived so a newly added closing
  // action cannot be skipped on cancel yet left skipped forever on revert.
  const normalDefaults = normalClosingActionDefaults(policies)

  newState = skipStepAction(newState, {
    currentEventIndex,
    stepAndActionKeys: closingActionKeys([
      ...CANCELLATION_CLOSING_ACTIONS,
      ...normalDefaults.filter(({ skip }) => skip).map(({ key }) => key),
    ]),
  })

  newState = unskipStepAction(newState, {
    currentEventIndex,
    stepAndActionKeys: closingActionKeys(
      normalDefaults.filter(({ skip }) => !skip).map(({ key }) => key),
    ),
  })

  // Uncheck all steps from closing step
  const closingStepKey = formatStepKey(
    ScriptStep.CLOSING,
    hasEventIndex ? currentEventIndex : undefined,
  )
  Object.values(state.steps[closingStepKey].actions).forEach(action => {
    newState = undoStepAction(newState, {
      step: closingStepKey,
      key: action.key,
    })
  })

  // Uncheck all steps from review step
  Object.values(state.steps[ScriptStep.REVIEW].actions).forEach(action => {
    newState = undoStepAction(newState, {
      step: ScriptStep.REVIEW,
      key: action.key,
    })
  })
  newState = unskipStepAction(newState, {
    stepAndActionKeys: FULLY_CANCELLED_REVIEW_ACTIONS,
  })

  // Reset values for closing step
  newState = updateEventResult(newState, null as never)
  newState = updateReasonForCna(newState, undefined)
  newState = updateOtherReason(newState, '')
  newState = updateNotes(newState, '')

  // Clearing timestamps in case they went on record during CNA,
  // because it prevented reporter from going on record during Opening step
  const firstSkippedStep = getBaseStepKey(targetStep)

  if (
    firstSkippedStep === ScriptStep.ATTENDANCE ||
    firstSkippedStep === ScriptStep.OPENING
  ) {
    newState = updateTimestamps(newState, '')
  }

  // Reset values for navigation
  newState = setFirstSkippedStep(newState, null, currentEventIndex ?? 0)
  newState = updateCurrentStep(newState, targetStep)
  newState = startStep(newState, targetStep)

  return newState
}

// cancel proceeding
export const cancelProceeding = (
  state: DRScriptData,
  {
    skipSteps,
    skipStepActions,
    unskipStepActions,
    first_skipped_step,
  }: {
    skipSteps: (ScriptStep | string)[]
    skipStepActions: StepAndActionKey[]
    unskipStepActions: StepAndActionKey[]
    first_skipped_step: string | null
  },
) => {
  const { currentEventIndex, hasEventIndex } = getCurrentEventState(state)
  let newState = { ...state }

  for (const step of skipSteps) {
    newState = skipStep(newState, step)
  }

  newState = skipStepAction(newState, {
    currentEventIndex,
    stepAndActionKeys: skipStepActions,
  })
  newState = unskipStepAction(newState, {
    stepAndActionKeys: unskipStepActions,
    currentEventIndex,
  })
  if (
    areAllEventsCancelledAfterCurrentCancellation(
      state,
      currentEventIndex,
      first_skipped_step,
    )
  ) {
    newState = skipStepAction(newState, {
      stepAndActionKeys: FULLY_CANCELLED_REVIEW_ACTIONS,
    })
  }
  const closingStepKey = formatStepKey(
    ScriptStep.CLOSING,
    hasEventIndex ? currentEventIndex : undefined,
  )
  newState = updateCurrentStep(newState, closingStepKey)
  newState = startStep(newState, closingStepKey)

  if (first_skipped_step !== null) {
    newState = setFirstSkippedStep(
      newState,
      first_skipped_step,
      currentEventIndex,
    )
  }

  return newState
}

export const updateGlobalState = (
  state: DRScriptData,
  data: Partial<DRScriptData>,
) => {
  return {
    ...state,
    ...data,
  } as DRScriptData
}

export const ALL_ACTIONS: StateActions = {
  completeStepAction,
  undoStepAction,
  updateAttendees,
  updateAttendeesAndCompleteAction,
  updateCurrentStep,
  startStep,
  completeStep,
  skipStep,
  updateNotes,
  addExhibit,
  updateExhibit,
  deleteExhibit,
  setNoExhibits,
  ensureExhibitsTracked,
  updateSpellings,
  updateTimestamps,
  updateAttendee,
  skipStepAction,
  addTimestamp,
  updateConductingData,
  unskipStepAction,
  updateOrderTranscript,
  updateReadWaiveAnswer,
  updateErrataRecipient,
  setFirstSkippedStep,
  updateEventResult,
  updateReasonForCna,
  updateOtherReason,
  updateCounty,
  updateState,
  updateLowConfidenceWords,
  completeStepAndContinue,
  cancelProceeding,
  updateGlobalState,
  incompleteStep,
  revertCanceledProceeding,
  updateLastTimestamp,
  updateCnaDeclaredAt,
  removeUnstartedWitnessSlice,
}
